import React from 'react'
import clsx from 'clsx'
import { makeStyles } from '@material-ui/core/styles'
import {
  Theme,
  Typography,
  Grid,
  Box,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from '@material-ui/core'
import { NgsoRoute, useRouter } from '@src/routes'
import UploadFile from '../../../components/UploadFile'
import { TabContext, TabList, TabPanel } from '@material-ui/lab'
import Tab from '@material-ui/core/Tab'
import messages from '../../../messages'
import { FormattedMessage, useIntl } from 'react-intl'
import DownloadTemplate from './DownloadTemplate'
import CopyEntry from './CopyEntry'
import {
  ActiveLinkType,
  ProductType,
  FileFormat,
  ProductUploadType,
  StartRow,
  UploadType,
} from '../enum'
import Button from '@material-ui/core/Button'
import {
  convertToASCII,
  checkMultipleLine,
} from '@src/routes/Configurators/utils/util'
import {
  useNgsoCopyPasteMutation,
  useNgsoFileUploadMutation,
  usePlateSessionFieldMutation,
  useUploadCopyPasteTextMutation,
  useFileUploadPlateMutation,
} from '@src/mutations/ConfiguratorTubesMutation.generated'
import CircleLoader from '@src/components/CircleLoader'
import NgsoValidationErrors from '../../../validations/NgsoValidationErrors'
import { configContext } from '../Ngso'
import { EmptyNgsoSession, EmptyWellPlate } from '../EmptySession'
import CautionPopup from './CautionPopup'
import StyledRadio from '@src/routes/Configurators/utils/ConfiguratorCustomRadioButton'

const useStyles = makeStyles((theme: Theme) => {
  return {
    container: {
      border: `1px solid ${theme.palette.grey[400]}`,
      padding: theme.spacing(6),
    },
    entryMethodHeader: {
      fontFamily: 'Lato',
      fontSize: theme.spacing(5),
      fontWeight: 900,
      lineHeight: '1.2',
      letterSpacing: '1.5px',
    },
    radioButtonSelection: {
      paddingTop: theme.spacing(5),
    },
    tubeButton: {
      marginRight: theme.spacing(16),
    },
    formControlLabel: {
      marginLeft: theme.spacing(-1),
      fontWeight: 900,
      fontFamily: 'Lato',
    },
    radio: {
      color: theme.palette.grey[500],
      '& .MuiSvgIcon-root:not(.MuiSvgIcon-root ~ .MuiSvgIcon-root)': {
        color: theme.palette.grey[500],
      },
      '& .MuiSvgIcon-root + .MuiSvgIcon-root': {
        color: theme.palette.primary.main,
      },
    },
    checked: {},
    tab: {
      textTransform: 'none',
      fontSize: theme.typography.pxToRem(15),
      fontWeight: 900,
      padding: theme.spacing(2, 0),
      marginRight: theme.spacing(5),
      minWidth: 'auto',
      color: theme.palette.primary.main,
      '& span': {
        alignItems: 'start',
      },
      opacity: 1,
    },
    selected: {
      color: theme.palette.common.black,
    },
    tabListIndicator: {
      '& .MuiTabs-indicator': {
        marginBottom: theme.spacing(2.5),
        backgroundColor: theme.palette.common.black,
      },
      '& .Mui-selected': {
        color: theme.palette.common.black,
      },
    },
    divider: {
      fontSize: theme.typography.pxToRem(20),
      lineHeight: theme.typography.pxToRem(48),
      color: theme.palette.grey[500],
      textAlign: 'center',
      marginRight: theme.spacing(5),
    },
    tabContent: {
      padding: theme.spacing(2, 0),
    },
    fileUploadHeadingTop: {
      fontSize: theme.typography.pxToRem(12),
      marginBottom: theme.spacing(6),
      fontFamily: 'Lato',
    },
    copyPasteHeadingTop: {
      marginBottom: theme.spacing(6),
      fontFamily: 'Lato',
      fontSize: theme.typography.pxToRem(12),
    },
    contentPadding: {
      paddingLeft: theme.spacing(7),
    },
    over1: {
      fontSize: theme.typography.pxToRem(14),
    },
    copyPasteContentPadding: {
      paddingLeft: theme.spacing(5),
    },
    buttonAlign: {
      textAlign: 'end',
      marginTop: theme.spacing(3),
    },
    checkboxBorder: {
      color: theme.palette.grey[500],
    },
    uploadButton: {
      backgroundColor: theme.palette.primary.main,
      minWidth: theme.spacing(25),
      color: theme.palette.common.white,
      '&:disabled': {
        color: theme.palette.common.white,
      },
      marginTop: theme.spacing(2),
    },
    downloadContainer: {
      marginBottom: theme.spacing(8),
    },
  }
})
export const Plate = {
  plateName: '',
  wellList: [],
  biTubeWellList: [],
}
interface OligosInput {
  row?: string
  column?: string
  name: string
  fiveMod?: string
  sequence: string
  threeMod?: string
  scale?: string
  format?: string
  comments?: string | null
}
const PlateName = ['1', '2', '3', '4', '5', '6', '7', '8']
export const parseSpreadsheet = async (
  file?: File,
  productType?: string
): Promise<string> =>
  new Promise((resolve, reject) => {
    if (!file) {
      return resolve('')
    }
    const reader = new FileReader()
    reader.onload = async (e) => {
      try {
        const plateList: any = []
        const XLSX = await import('xlsx')
        const data = new Uint8Array((e.target as any).result)
        const workbook = XLSX.read(data, { type: 'array' })
        let products: OligosInput[] = []
        let mergedProducts = ''
        const optionsSelect = 'Click for Options'
        if (productType === ProductType.NgsoTubes) {
          const sheetName = workbook.SheetNames[0]
          const worksheet = workbook.Sheets[sheetName]
          const hasHeaders = true
          if (
            sheetName !== undefined &&
            worksheet['A12']?.v === ProductUploadType.Name &&
            worksheet['B12']?.v === ProductUploadType.Mod_5 &&
            worksheet['C12']?.v === ProductUploadType.Sequence &&
            worksheet['D12']?.v === ProductUploadType.Mod_3 &&
            worksheet['E12']?.v === ProductUploadType.Scale &&
            worksheet['F12']?.v === ProductUploadType.Format &&
            worksheet['G12']?.v.includes(ProductUploadType.Comments) &&
            worksheet['H12'] === undefined
          ) {
            worksheet['!ref'] = 'A1:G215'

            products = (
              XLSX.utils.sheet_to_json(worksheet, {
                header: [
                  'name',
                  'fiveMod',
                  'sequence',
                  'threeMod',
                  'scale',
                  'format',
                  'comments',
                ],
                raw: false,
                blankrows: true,
                defval: '',
              }) as OligosInput[]
            ).map((oligo, index) => {
              let {
                name,
                fiveMod,
                sequence,
                threeMod,
                scale,
                format,
                comments,
              } = oligo

              name = name ? name.toString().trim() : ''
              fiveMod = fiveMod ? fiveMod.toString().trim() : ''
              sequence = sequence ? sequence.toString().trim() : ''
              threeMod = threeMod ? threeMod.toString().trim() : ''
              scale =
                scale && optionsSelect !== scale ? scale.toString().trim() : ''
              format =
                format && optionsSelect !== format
                  ? format.toString().trim()
                  : ''
              comments = comments ? comments.toString().trim() : ''
              if (index > 12 && (scale.length > 0 || format.length > 0)) {
                resolve(FileFormat.ValidateInput)
              }
              return {
                name,
                fiveMod,
                sequence,
                threeMod,
                scale,
                format,
                comments,
              }
            })
            if (hasHeaders) {
              products.splice(0, 12)
            }
          } else {
            resolve(FileFormat.InvalidTemplate)
          }
          products.forEach((product, index) => {
            let val = ''
            for (const value in product) {
              value !== 'comments'
                ? (val += `${product[value]}\t`)
                : (val += `${product[value]}`)
            }
            index !== products.length - 1
              ? (mergedProducts += `${val}\n`)
              : (mergedProducts += `${val}`)
          })

          resolve(mergedProducts)
        } else if (productType === ProductType.NgsoPlates) {
          PlateName.forEach((nameforSheet, index) => {
            const sheetName = workbook.SheetNames[index]
            const worksheet = workbook.Sheets[sheetName]
            const hasHeaders = true
            if (
              sheetName !== ProductUploadType.platesSpecification &&
              sheetName !== ProductUploadType.Trademark &&
              sheetName !== undefined
            ) {
              if (
                worksheet['A11']?.v === ProductUploadType.Row &&
                worksheet['B11']?.v === ProductUploadType.Column &&
                worksheet['C11']?.v === ProductUploadType.Name &&
                worksheet['D11']?.v === ProductUploadType.Mod_5 &&
                worksheet['E11']?.v === ProductUploadType.PlatesSequence &&
                worksheet['F11']?.v === ProductUploadType.Mod_3 &&
                worksheet['G11']?.v === ProductUploadType.Format &&
                worksheet['H11']?.v === ProductUploadType.PlatesComments &&
                worksheet['I11'] === undefined
              ) {
                const emptyPlate = Object.assign({}, Plate)
                plateList.push(emptyPlate)
                plateList[index].plateName = sheetName
                worksheet['!ref'] = 'A1:H210'

                products = (
                  XLSX.utils.sheet_to_json(worksheet, {
                    header: [
                      'row',
                      'column',
                      'name',
                      'fiveMod',
                      'sequence',
                      'threeMod',
                      'format',
                      'comments',
                    ],
                    raw: false,
                  }) as OligosInput[]
                ).map((oligo) => {
                  let {
                    row,
                    column,
                    name,
                    fiveMod,
                    sequence,
                    threeMod,
                    scale,
                    format,
                    comments,
                  } = oligo

                  row = row ? row.toString().trim() : ''
                  column = column ? column.toString().trim() : ''
                  name = name ? name.toString().trim() : ''
                  fiveMod = fiveMod ? fiveMod.toString().trim() : ''
                  sequence = sequence ? sequence.toString().trim() : ''
                  threeMod = threeMod ? threeMod.toString().trim() : ''
                  format =
                    format && optionsSelect !== format
                      ? format.toString().trim()
                      : ''
                  comments = comments ? comments.toString().trim() : ''

                  return {
                    row,
                    column,
                    name,
                    fiveMod,
                    sequence,
                    threeMod,
                    scale,
                    format,
                    comments,
                  }
                })
                if (hasHeaders) {
                  //for deleting the extra row from mixed template
                  if (products[7].row === ProductUploadType.Row) {
                    products.splice(0, 8)
                  } else {
                    products.splice(0, 7)
                  }
                  checkMultipleLine(products, resolve)
                }
              } else {
                resolve(FileFormat.InvalidTemplate)
              }
              plateList[index].wellList = products
              plateList[index].biTubeWellList = []
            }
          })
          resolve(plateList)
        }
      } catch (err) {
        reject(err)
      }
    }
    reader.readAsArrayBuffer(file)
  })

const EntryMethod = (props) => {
  const intl = useIntl()
  const classes = useStyles()
  const router = useRouter()
  const { state, dispatch } = React.useContext(configContext)
  const [value, setValue] = React.useState<string>(state.uploadType)
  const [text, setText] = React.useState('')
  const [disableUpload, setDisableUpload] = React.useState(true)
  const [override, setOverride] = React.useState(false)
  const [validationMessage, setValidationMessage] = React.useState<string[]>([])
  const [dialogOpen, setDialogOpen] = React.useState(false)
  const [isProductChange, setIsProductChange] = React.useState(false)
  const [uploadDataType, setUploadDataType] = React.useState('')
  const [selectedProduct, setSelectedProduct] = React.useState('ngsotubes')
  const [datavalue, setDatavalue] = React.useState<any>('')
  const { productDetails, submitHandler } = props
  const { product } = router.query

  let { sessionData, isPageLoad } = state
  const [selectedType, setSelectedType] = React.useState(
    sessionData[0].productType
  )
  const [fileUploadPlate, { loading: platefileUploadLoading }] =
    useFileUploadPlateMutation()
  const [uploadPlateCopyPasteText, { loading: copypastePlateLoading }] =
    useUploadCopyPasteTextMutation()
  const [uploadCopyPasteText, { loading: copypasteLoading }] =
    useNgsoCopyPasteMutation()
  const [fileupload, { loading: fileUploadLoading }] =
    useNgsoFileUploadMutation()
  const [ngsosessionField] = usePlateSessionFieldMutation()

  const saveNGSODataToSession = async (obj) => {
    await ngsosessionField({
      variables: { input: obj, productInput: productDetails },
    })
  }
  const savingProductTypeToSession = (product) => {
    const emptyStateObj = Object.assign({}, EmptyNgsoSession)
    sessionData = [emptyStateObj]
    sessionData.forEach((data) => {
      data.productType = product
      delete data.__typename
      data.wellList.forEach((well) => {
        delete well.__typename
      })
    })
    dispatch({ type: 'SET_STEP_NO', data: 0 })
    dispatch({
      type: 'UPDATE_NGSO_INPUT',
      data: [...sessionData],
    })
    saveNGSODataToSession(sessionData)
  }
  React.useEffect(() => {
    if (isPageLoad) {
      product && setSelectedType(product)
      setValue(state.uploadType)
    }
    if (state.errorFromConfirmOrderPage?.length !== 0) {
      setValidationMessage([state.errorFromConfirmOrderPage])
    }
  }, [isPageLoad, product])

  const updateProductChange = (product: string) => {
    setSelectedType(product)
    setValidationMessage([])
    dispatch({ type: 'SET_ERROR_FROM_CONFIRM_ORDER', data: '' })
    setText('')
    setDisableUpload(true)
    const path = `${NgsoRoute.index()}?product=${product}&activeLink=${
      ActiveLinkType.EntryMethod
    }`
    router.push(path)
  }

  const handleTypeChange = (product: string) => {
    const hasProductData = sessionData.find((item) => item.pageNo === 1)
    if (hasProductData) {
      setSelectedProduct(product)
      setDialogOpen(true)
      setIsProductChange(true)
    } else {
      setDialogOpen(false)
      setOverride(false)
      savingProductTypeToSession(product)
      updateProductChange(product)
      submitHandler([])
    }
  }

  const onCloseCautionPopup = () => {
    setDialogOpen(false)
  }

  const handleChange = (event, newValue) => {
    setValue(newValue)
    dispatch({ type: 'SET_UPLOAD_TYPE', data: newValue })
    dispatch({ type: 'SET_ERROR_FROM_CONFIRM_ORDER', data: '' })
    setValidationMessage([])
    setText('')
    setDisableUpload(true)
    const hasProductData = sessionData.find((item) => item.pageNo === 1)
    if (!hasProductData) {
      setOverride(false)
    }
  }

  const formatWellListData = (wellListItem) => {
    const WellListData: any = []
    wellListItem.wellList.forEach((data: any, indexWell: number) => {
      const emptyWellObj = Object.assign({}, EmptyWellPlate)
      WellListData.push(emptyWellObj)
      WellListData[indexWell].amount = data.amount
      WellListData[indexWell].baseCount = data.baseCount
      if (data.comments) {
        WellListData[indexWell].comments = data.comments
      } else {
        WellListData[indexWell].comments = ''
      }
      WellListData[indexWell].column = data.column
      WellListData[indexWell].crossContamination = data.crossContamination
      WellListData[indexWell].countryCode = data.countryCode
      WellListData[indexWell].concentration = data.concentration
      WellListData[indexWell].currency = data.currency
      WellListData[indexWell].currencySymbol = data.currencySymbol
      WellListData[indexWell].epsilon = data.epsilon
      WellListData[indexWell].fiveMod = data.fiveMod
      WellListData[indexWell].format = data.format
      WellListData[indexWell].gcContent = data.gcContent
      WellListData[indexWell].id = data.id
      WellListData[indexWell].isOverridden = data.isOverridden
      WellListData[indexWell].listError = data.listError
      WellListData[indexWell].listWarning = data.listWarning
      WellListData[indexWell].listAdditionalServices =
        data.listAdditionalServices
      WellListData[indexWell].listNgso = data.listNgso
      WellListData[indexWell].listISO = data.listISO
      if (data.analyticalHPLC) {
        WellListData[indexWell].analyticalHPLC = data.analyticalHPLC
      } else if (productDetails.isCSRSupervisor) {
        WellListData[indexWell].analyticalHPLC = ''
      } else {
        WellListData[indexWell].analyticalHPLC = null
      }
      if (data.certificateOfAnalysis) {
        WellListData[indexWell].certificateOfAnalysis =
          data.certificateOfAnalysis
      } else if (productDetails.isCSRSupervisor) {
        WellListData[indexWell].certificateOfAnalysis = ''
      } else {
        WellListData[indexWell].certificateOfAnalysis = null
      }
      WellListData[indexWell].meltingTemparature = data.meltingTemparature
      WellListData[indexWell].molecularWeight = data.molecularWeight
      WellListData[indexWell].name = data.name
      if (data.ngsoType) {
        WellListData[indexWell].ngsoType = data.ngsoType
      } else {
        WellListData[indexWell].ngsoType = ''
      }
      WellListData[indexWell].oligoId = data.id
      WellListData[indexWell].orderItemId = data.orderItemId
      WellListData[indexWell].packageType = data.packageType
      WellListData[indexWell].pageNo = data.pageNo
      WellListData[indexWell].price = data.price
      WellListData[indexWell].priceHeaderError = data.priceHeaderError
      WellListData[indexWell].primerDimer = data.primerDimer
      WellListData[indexWell].productType = data.productType
      WellListData[indexWell].purification = data.purification
      WellListData[indexWell].qualitySystem = data.qualitySystem
      WellListData[indexWell].row = data.row
      WellListData[indexWell].scale = data.scale
      WellListData[indexWell].secondaryStructure = data.secondaryStructure
      WellListData[indexWell].sequence = data.sequence
      WellListData[indexWell].shipping = data.shipping
      if (data.threeMod) {
        WellListData[indexWell].threeMod = data.threeMod
      } else {
        WellListData[indexWell].threeMod = ''
      }
      WellListData[indexWell].tubes = data.tubes
      WellListData[indexWell].technicalSequence = data.technicalSequence
    })
    return WellListData
  }

  const handleText = (e) => {
    if (e.target.value.length !== 0) {
      setDisableUpload(false)
    } else {
      setDisableUpload(true)
    }
    dispatch({ type: 'SET_ERROR_FROM_CONFIRM_ORDER', data: '' })
    setValidationMessage([])
    setText(e.target.value)
  }

  const setTubeData = (obj) => {
    const emptyStateObj = Object.assign({}, EmptyNgsoSession)
    sessionData = [emptyStateObj]
    obj.forEach((item: any, index: number) => {
      if (index > 0) {
        const emptyPlate = Object.assign({}, EmptyNgsoSession)
        sessionData.push(emptyPlate)
      }
      sessionData[index].applyToAll = item.applyToAll
      sessionData[index].channelType = item.channelType
      sessionData[index].countOfWells = item.countOfWells
      sessionData[index].isAmountSelected = item.isAmountSelected
      sessionData[index].isCSR = item.isCSR
      sessionData[index].isCSRSupervisor = item.isCSRSupervisor
      sessionData[index].isConcentrationOther = item.isConcentrationOther
      sessionData[index].isPackageTypeOther = item.isPackageTypeOther
      sessionData[index].isShippingOverridden = item.isShippingOverridden
      sessionData[index].isSpecificationsOverridden =
        item.isSpecificationsOverridden
      sessionData[index].totalBaseCount = item.totalBaseCount
      if (item.countryCode) {
        sessionData[index].countryCode = item.countryCode
      } else {
        sessionData[index].countryCode = productDetails.countryCode
      }
      sessionData[index].plateId = item.plateId
      sessionData[index].plateType = item.plateType
      sessionData[index].plateName = item.plateName
      sessionData[index].productType = item.productType
      sessionData[index].pageNo = item.pageNo
      sessionData[index].material = item.material
      sessionData[index].wellType = item.wellType
      sessionData[index].biTubeWellList = []
      sessionData[index].wellList = formatWellListData(item)
    })
    sessionData.forEach((data) => {
      data.pageNo = 1
      delete data.__typename
      data.wellList.forEach((well) => {
        delete well.__typename
      })
    })
    dispatch({ type: 'SET_STEP_NO', data: 1 })
    dispatch({
      type: 'UPDATE_NGSO_INPUT',
      data: [...sessionData],
    })
  }

  const performCopyPaste = (key, value) => {
    setValidationMessage([])
    if (key && key !== '') {
      if (key === messages.CONF_SUCCESS.defaultMessage && value) {
        const obj: any = JSON.parse(value)
        setTubeData(obj)
        const path = `${NgsoRoute.index()}?product=${selectedType}&activeLink=${
          ActiveLinkType.ConfirmOrder
        }`
        router.push(path)
      } else {
        savingProductTypeToSession(productDetails.type)
        let errors = key.trim()
        if (errors.endsWith(';')) {
          errors = errors.slice(0, -1)
        }
        const error = errors.split(';')
        setValidationMessage(error)
        if (error) submitHandler(error)
        setText('')
        setDisableUpload(true)
        window.scrollTo(0, 0)
      }
    }
  }

  const copyPaste = async (obj) => {
    const response = await uploadCopyPasteText({
      variables: {
        input: obj,
        productInput: productDetails,
        isOverride: override,
        isDForm: false,
      },
    })
    const { data } = response
    if (data && data.ngsoCopyPaste) {
      if (!data.ngsoCopyPaste.serverError) {
        const { key, value } = data.ngsoCopyPaste
        performCopyPaste(key, value)
      } else {
        window.scrollTo(0, 0)
      }
    }
  }
  const plateCopyPaste = async (obj) => {
    const response = await uploadPlateCopyPasteText({
      variables: {
        input: obj,
        productInput: productDetails,
        isOverride: override,
        autogenerateAntisense: false,
      },
    })
    const { data } = response
    if (data && data.uploadCopyPasteText) {
      if (!data.uploadCopyPasteText.serverError) {
        const { key, value } = data.uploadCopyPasteText
        performCopyPaste(key, value)
      } else {
        window.scrollTo(0, 0)
      }
    }
  }
  const handleUploadChange = async (file) => {
    dispatch({ type: 'SET_ERROR_FROM_CONFIRM_ORDER', data: '' })
    setValidationMessage([])
    let error
    if (
      FileFormat.AcceptFormat &&
      FileFormat.AcceptFormat.split(',')
        .map((type) => type.trim().toLowerCase())
        .includes(file?.type)
    ) {
      let inputStr: any = ''
      inputStr = await parseSpreadsheet(file, productDetails.type)
      const startRow = parseInt(StartRow[productDetails.type])
      if (inputStr === FileFormat.ValidateInput) {
        error = intl.formatMessage(
          messages.CONF_NGSO_FILE_UPLOAD_VALIDATION_ERROR
        )
      }
      if (inputStr === FileFormat.InvalidTemplate) {
        error = intl.formatMessage(messages.CONF_INVALID_TEMPLATE)
      }
      if (inputStr === '') {
        error = intl.formatMessage(messages.CONF_EMPTY_FILE)
      }
      if (inputStr === FileFormat.MultipleLineError) {
        error = intl.formatMessage(messages.MULTIPLE_LINE_ERROR)
      }
      if (
        inputStr !== '' &&
        inputStr !== FileFormat.InvalidTemplate &&
        inputStr !== FileFormat.MultipleLineError &&
        inputStr !== FileFormat.ValidateInput
      ) {
        // make api call here passing inputStr as input
        if (productDetails.type === ProductType.NgsoTubes) {
          const response = await fileupload({
            fetchPolicy: 'no-cache',
            variables: {
              input: inputStr,
              productInput: productDetails,
              isOverride: override,
              isDForm: false,
              startRow: startRow,
            },
          })
          const { data } = response
          if (data && data.ngsoFileUpload) {
            if (!data.ngsoFileUpload.serverError) {
              const { key, value } = data.ngsoFileUpload
              performCopyPaste(key, value)
            } else {
              window.scrollTo(0, 0)
            }
          }
        } else {
          const response = await fileUploadPlate({
            fetchPolicy: 'no-cache',
            variables: {
              input: inputStr,
              productInput: productDetails,
              isOverride: override,
              autogenerateAntisense: false,
              startRow: startRow,
            },
          })
          const { data } = response

          if (data && data.fileUploadPlate) {
            if (!data.fileUploadPlate.serverError) {
              const { key, value } = data.fileUploadPlate
              performCopyPaste(key, value)
            } else {
              window.scrollTo(0, 0)
            }
          }
        }
      }
    } else {
      if (file !== undefined) {
        error = intl.formatMessage(messages.CONF_INVALID_FORMAT)
      }
    }
    if (error) {
      savingProductTypeToSession(productDetails.type)
      setValidationMessage([error])
      submitHandler([error])
    }
  }

  const validateUploadFile = (file) => {
    const hasProductData = sessionData[0].wellList.find((item) => !!item.name)
    if (!hasProductData) {
      setText('')
      handleUploadChange(file)
      setDisableUpload(true)
    } else {
      setDialogOpen(true)
      setUploadDataType(UploadType.fileUpload)
      setDatavalue(file)
    }
  }

  const handleSubmit = () => {
    const hasProductData = sessionData[0].wellList.find((item) => !!item.name)
    if (text !== null) {
      if (productDetails.type === ProductType.NgsoTubes) {
        if (!hasProductData) {
          copyPaste(convertToASCII(text))
        } else {
          setDialogOpen(true)
          setDatavalue(text)
          setUploadDataType(UploadType.copyPaste)
        }
      } else {
        if (!hasProductData) {
          plateCopyPaste(convertToASCII(text))
        } else {
          setDialogOpen(true)
          setDatavalue(text)
          setUploadDataType(UploadType.copyPaste)
        }
      }
    }
  }

  const overrideSession = (product: string, data, type, isProductChange) => {
    if (isProductChange) {
      setDialogOpen(false)
      savingProductTypeToSession(product)
      updateProductChange(product)
      setIsProductChange(false)
    } else {
      if (type === UploadType.copyPaste) {
        if (productDetails.type === ProductType.NgsoTubes) {
          copyPaste(convertToASCII(data))
        } else {
          plateCopyPaste(convertToASCII(data))
        }
      } else {
        handleUploadChange(data)
        setText('')
        setDisableUpload(true)
      }
    }
    submitHandler([])
  }

  const handleOverride = (value) => {
    setOverride(value)
    setValidationMessage([])
    submitHandler([])
  }
  const downloadtemplateAndOverride = () => {
    return (
      <>
        <Grid container sm={8}>
          <DownloadTemplate
            productType={selectedType}
            productDetails={productDetails}
          />
        </Grid>

        {productDetails.isCSRSupervisor ? (
          <FormControlLabel
            classes={{ label: classes.over1 }}
            control={
              <Checkbox
                value={override}
                checked={override}
                className={classes.checkboxBorder}
                onChange={() => {
                  handleOverride(!override)
                }}
              />
            }
            label={messages.CONF_OVERRIDE_VALIDATION.defaultMessage}
          />
        ) : (
          ''
        )}
      </>
    )
  }
  return (
    <Box className={classes.container}>
      <Grid container>
        <Grid item xs={12} className={classes.entryMethodHeader}>
          <FormattedMessage {...messages.SELECT_AN_ENTRY_METHOD} />
        </Grid>
        <Grid item xs={12} className={classes.radioButtonSelection}>
          <RadioGroup
            row
            aria-label="SelectMethod"
            name="row-radio-buttons-group"
            defaultValue={ProductType.NgsoTubes}
            value={selectedType}
            onChange={(e) => handleTypeChange(e.target.value)}
          >
            <FormControlLabel
              className={classes.tubeButton}
              value={ProductType.NgsoTubes}
              control={
                <StyledRadio
                  isConfiguratorsRedesign="true"
                  classes={{ root: classes.radio, checked: classes.checked }}
                />
              }
              label={
                <Typography className={classes.formControlLabel}>
                  <FormattedMessage {...messages.CONF_NGSO_TUBE} />
                </Typography>
              }
            />
            <FormControlLabel
              value={ProductType.NgsoPlates}
              control={
                <StyledRadio
                  isConfiguratorsRedesign="true"
                  classes={{ root: classes.radio, checked: classes.checked }}
                />
              }
              label={
                <Typography className={classes.formControlLabel}>
                  <FormattedMessage {...messages.CONF_NGSO_PLATE} />
                </Typography>
              }
            />
          </RadioGroup>
        </Grid>
      </Grid>
      <TabContext value={value}>
        <Box>
          <TabList onChange={handleChange} className={classes.tabListIndicator}>
            <Tab
              label={<FormattedMessage {...messages.CONF_NGSO_FILE_UPLOAD} />}
              value={UploadType.fileUpload}
              className={clsx(classes.tab, {
                [classes.selected]: value === UploadType.fileUpload,
              })}
            />
            <span className={classes.divider}>|</span>
            <Tab
              label={<FormattedMessage {...messages.CONF_NGSO_COPY_PASTE} />}
              value={UploadType.copyPaste}
              className={clsx(classes.tab, {
                [classes.selected]: value === UploadType.copyPaste,
              })}
            />
          </TabList>
        </Box>
        <TabPanel value={UploadType.fileUpload} className={classes.tabContent}>
          <Grid container direction="row">
            <Grid item xs={12} className={classes.fileUploadHeadingTop}>
              <FormattedMessage
                {...messages.CONF_NGSO_FILE_UPLOAD_HEADING_TEXT}
              />
            </Grid>
            {selectedType === ProductType.NgsoPlates && (
              <Grid item sm={12} className={classes.downloadContainer}>
                {downloadtemplateAndOverride()}
              </Grid>
            )}
            <Grid item sm={4}>
              <UploadFile
                accept={FileFormat.AcceptFormat}
                handleUploadChange={([file]) => validateUploadFile(file)}
              />
              <NgsoValidationErrors errors={validationMessage} />
            </Grid>
            {selectedType === ProductType.NgsoTubes && (
              <Grid item xs={8} className={classes.contentPadding}>
                {downloadtemplateAndOverride()}
              </Grid>
            )}
          </Grid>
        </TabPanel>
        <TabPanel value={UploadType.copyPaste} className={classes.tabContent}>
          <Grid container direction="row">
            <Grid item xs={12} className={classes.copyPasteHeadingTop}>
              <FormattedMessage
                {...messages.CONF_NGSO_COPY_PASTE_HEADING_TEXT}
              />
              <br />
              <FormattedMessage
                {...messages[
                  `CONF_NGSO_COPY_PASTE_EXAMPLE_${productDetails.type}`
                ]}
              />
            </Grid>
            {selectedType === ProductType.NgsoPlates && (
              <Grid item xs={12} className={classes.downloadContainer}>
                {downloadtemplateAndOverride()}
              </Grid>
            )}
            <Grid item sm={6}>
              <CopyEntry
                handlechange={handleText}
                text={text}
                productDetails={productDetails}
              />
              <NgsoValidationErrors errors={validationMessage} />
              <Grid item xs={12} className={classes.buttonAlign}>
                <Button
                  variant="contained"
                  color="primary"
                  size="large"
                  className={classes.uploadButton}
                  type="submit"
                  onClick={handleSubmit}
                  disabled={disableUpload}
                >
                  <FormattedMessage {...messages.SUBMIT} />
                </Button>
              </Grid>
            </Grid>
            {selectedType === ProductType.NgsoTubes && (
              <Grid item xs={6} className={classes.copyPasteContentPadding}>
                {downloadtemplateAndOverride()}
              </Grid>
            )}
          </Grid>
        </TabPanel>
      </TabContext>

      <CautionPopup
        dialogOpen={dialogOpen}
        setDialogOpen={setDialogOpen}
        uploadDataType={uploadDataType}
        continueActionHandler={overrideSession}
        onClose={onCloseCautionPopup}
        data={datavalue}
        product={selectedProduct}
        isProductChange={isProductChange}
      />

      {(fileUploadLoading ||
        copypasteLoading ||
        copypastePlateLoading ||
        platefileUploadLoading) && <CircleLoader />}
    </Box>
  )
}
export default EntryMethod
